import type { StudyCategorySlug } from 'content/science/studyTypes';

/* Per-category color palette. Restrained tones, each picked so the
 * pill reads as a tag without competing with the title. Background
 * is a very light tint, text is the saturated tone, border matches
 * text at ~24% opacity.
 *
 * Add a new category by adding a slug → tokens row here. Don't
 * color-code inline elsewhere — every consumer reads from this map. */

export interface CategoryColor {
  bg: string;
  fg: string;
  border: string;
}

/* Sourced from the Ultrahuman colour system (Color System v0.1).
 *
 * Every hue in that system ships the same ramp, and two of its steps are
 * named for exactly this job: **50 — "bg/wash"** and **700 — "text on
 * light"**. So each tag is its hue's 50 behind its hue's 700, which is the
 * pairing the system was built to guarantee contrast for. Nothing here is
 * hand-mixed.
 *
 * This replaces an ad-hoc Tailwind-ish palette whose heart-health red
 * (#DC2626 on #FEF2F2) read as an error state rather than a category —
 * alarming on a page where every tag is neutral information.
 *
 * Hue assignment avoids red entirely:
 *
 *   sleep             indigo   night, and the calmest hue in the system
 *   heart-health      magenta  cardiovascular, without the alarm of red
 *   metabolic-health  orange   glucose and energy
 *   womens-health     teal     Ultrahuman's core brand hue
 *   sports            blue     performance
 *   environment       green    the obvious natural fit
 *   longevity         violet   biological age; the cluster's own hue
 *   other             neutral
 *
 * Borders are the 700 at 18%, unchanged in method from before. */
export const CATEGORY_COLORS: Record<StudyCategorySlug, CategoryColor> = {
  sleep: {
    bg: '#E3E8FE',
    fg: '#0A22AD',
    border: 'rgba(10, 34, 173, 0.18)',
  },
  'heart-health': {
    bg: '#FBE5ED',
    fg: '#8E1849',
    border: 'rgba(142, 24, 73, 0.18)',
  },
  'metabolic-health': {
    bg: '#FFEFE7',
    fg: '#C44315',
    border: 'rgba(196, 67, 21, 0.18)',
  },
  environment: {
    bg: '#E5FBEF',
    fg: '#1E9A55',
    border: 'rgba(30, 154, 85, 0.18)',
  },
  sports: {
    bg: '#E6EEFD',
    fg: '#1F5DBE',
    border: 'rgba(31, 93, 190, 0.18)',
  },
  'womens-health': {
    bg: '#E1F7F8',
    fg: '#017376',
    border: 'rgba(1, 115, 118, 0.18)',
  },
  /* Violet, the same pair AREA_TONE.longevity uses on the studies page, so one
     subject is not two colours across the section. Indigo, magenta, orange,
     teal, blue and green are all spoken for above; violet is furthest from
     each. Contrast 6.59:1 — mid-range for this table. */
  longevity: {
    bg: '#F1E8FD',
    fg: '#6B25C4',
    border: 'rgba(107, 37, 196, 0.18)',
  },
  other: {
    bg: '#F1F1F1',
    fg: '#4A4A4A',
    border: 'rgba(74, 74, 74, 0.18)',
  },
};

export function colorsForCategory(slug: StudyCategorySlug): CategoryColor {
  return CATEGORY_COLORS[slug] || CATEGORY_COLORS.other;
}
